import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  Shield, CheckCircle, AlertCircle, FileCheck, Lock, Scale, 
  Eye, AlertTriangle, ExternalLink, Phone, Mail, Building2,
  Clock, Gavel, Briefcase, BookOpen, Award, ShieldCheck,
  FileText, Users, TrendingUp, ChevronRight, Sparkles, Globe,
  Target, Layers, Database, Cpu
} from 'lucide-react';
import { complianceContent, contactInfo, companyInfo } from '../data/mock';

const ComplianceUltra = () => {
  const [activeDisclaimer, setActiveDisclaimer] = useState(null);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  // Background image URLs
  const bgImages = {
    hero: "https://images.unsplash.com/photo-1762429648253-b8bc921c8b30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBjaXR5JTIwc2t5bGluZSUyMG5pZ2h0JTIwbGlnaHRzfGVufDB8fHx8MTc2OTMwOTU4NHww&ixlib=rb-4.1.0&q=85",
    regulatory: "https://images.unsplash.com/photo-1760129744728-740e32a95f73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBjaXR5JTIwc2t5bGluZSUyMG5pZ2h0JTIwbGlnaHRzfGVufDB8fHx8MTc2OTMwOTU4NHww&ixlib=rb-4.1.0&q=85",
    network: "https://images.unsplash.com/photo-1664854953181-b12e6dda8b7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGZpbmFuY2lhbCUyMGRhdGElMjBuZXR3b3JrJTIwY29ubmVjdGlvbnxlbnwwfHx8fDE3NjkzMDk1OTJ8MA&ixlib=rb-4.1.0&q=85"
  };

  return (
    <div className="page-container" ref={containerRef} data-testid="compliance-page">
      {/* Ultra Premium Hero with Background Image */}
      <section className="compliance-hero-ultra">
        <motion.div 
          className="compliance-hero-bg-image"
          style={{ 
            backgroundImage: `url(${bgImages.hero})`,
            y,
            scale
          }}
        />
        <div className="compliance-hero-overlay" />
        
        <div className="compliance-cosmic-bg">
          <div className="legal-orb orb-1"></div>
          <div className="legal-orb orb-2"></div>
          <div className="legal-orb orb-3"></div>
          <div className="legal-pattern"></div>
          
          {/* Floating shield icons */}
          <div className="floating-legal-icons">
            {[Shield, Scale, Gavel, FileCheck, Lock].map((Icon, i) => (
              <motion.div
                key={i}
                className="floating-legal-icon"
                style={{
                  left: `${10 + i * 20}%`,
                  top: `${20 + (i % 3) * 25}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  rotate: [0, 10, -10, 0],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: 5 + i,
                  repeat: Infinity,
                  delay: i * 0.5,
                }}
              >
                <Icon size={40} />
              </motion.div>
            ))}
          </div>

          {/* Particle effect */}
          <div className="shield-particles">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="shield-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -60, 0],
                  opacity: [0, 0.8, 0],
                  scale: [0, 1.5, 0],
                }}
                transition={{
                  duration: 4 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                }}
              />
            ))}
          </div>
        </div>

        <motion.div 
          className="compliance-hero-content"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="hero-badge-compliance"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", bounce: 0.6, delay: 0.2 }}
          >
            <Sparkles size={20} className="badge-sparkle" />
            Legal & Compliance Framework
          </motion.div>

          <motion.h1 
            className="display-huge gradient-text-compliance"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {complianceContent.title}
          </motion.h1>

          <motion.p 
            className="hero-description-compliance"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {complianceContent.introduction}
          </motion.p>

          <motion.div 
            className="compliance-badges-bar"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            {[
              { icon: <ShieldCheck size={32} />, label: "SEBI", status: "Compliant", color: "#43e97b" },
              { icon: <FileCheck size={32} />, label: "MCA", status: "Registered", color: "#4facfe" },
              { icon: <Lock size={32} />, label: "RBI", status: "Aligned", color: "#fa709a" }
            ].map((badge, i) => (
              <motion.div
                key={i}
                className="compliance-badge-item"
                whileHover={{ y: -10, scale: 1.08 }}
                animate={{ y: [0, -8, 0] }}
                transition={{ 
                  y: { duration: 3, repeat: Infinity, delay: i * 0.5 },
                  hover: { duration: 0.2 }
                }}
              >
                <div className="badge-icon-wrapper" style={{ background: badge.color }}>{badge.icon}</div>
                <div className="badge-text">
                  <span className="badge-label">{badge.label}</span>
                  <span className="badge-status">{badge.status}</span>
                </div>
                <motion.div 
                  className="badge-checkmark"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                >
                  <CheckCircle size={24} />
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div 
          className="scroll-indicator-ultra"
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{ display: "none" }}
        >
          <div className="scroll-line-ultra"></div>
          <span className="scroll-text">Learn more</span>
        </motion.div>
      </section>

      {/* Legal Structure & Philosophy with Images */}
      <section className="legal-structure-section">
        <div className="legal-structure-bg-image" style={{ backgroundImage: `url(${bgImages.network})` }} />
        <div className="legal-structure-overlay" />
        
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label-center">Foundation</span>
            <h2 className="display-medium">Legal Framework</h2>
            <p className="section-subtitle">Structural compliance and operational governance</p>
          </motion.div>

          <motion.div
            className="legal-grid-ultra"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.3 } }
            }}
          >
            <motion.div 
              className="legal-card-mega structure"
              variants={{
                hidden: { x: -100, opacity: 0 },
                visible: { x: 0, opacity: 1, transition: { duration: 0.8 } }
              }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="card-glow-legal"></div>
              <div className="legal-card-image">
                <Layers size={80} />
              </div>
              <div className="legal-icon-mega structure-icon">
                <Building2 size={48} />
              </div>
              <h2 className="heading-1">{complianceContent.structure.title}</h2>
              <p className="body-large legal-desc">{complianceContent.structure.content}</p>
              <div className="legal-features">
                {["Companies Act, 2013", "Governance-First", "Statutory Adherence"].map((feature, i) => (
                  <motion.div 
                    key={i} 
                    className="legal-feature-tag"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <CheckCircle size={16} />
                    {feature}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="legal-card-mega philosophy"
              variants={{
                hidden: { x: 100, opacity: 0 },
                visible: { x: 0, opacity: 1, transition: { duration: 0.8 } }
              }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="card-glow-legal"></div>
              <div className="legal-card-image">
                <Target size={80} />
              </div>
              <div className="legal-icon-mega philosophy-icon">
                <Scale size={48} />
              </div>
              <h2 className="heading-1">{complianceContent.philosophy.title}</h2>
              <p className="body-large legal-desc">{complianceContent.philosophy.content}</p>
              <div className="legal-features">
                {["Engineered Governance", "Systemic Auditability", "Operational Transparency"].map((feature, i) => (
                  <motion.div 
                    key={i} 
                    className="legal-feature-tag"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <CheckCircle size={16} />
                    {feature}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Regulatory Alignment with Background */}
      <section className="regulatory-section">
        <div className="regulatory-bg-image" style={{ backgroundImage: `url(${bgImages.regulatory})` }} />
        <div className="regulatory-overlay" />
        
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label-center">Statutory Oversight</span>
            <h2 className="display-medium">{complianceContent.regulations.title}</h2>
            <p className="section-subtitle">Adherence to Indian regulatory and statutory frameworks</p>
          </motion.div>

          <motion.div 
            className="regulatory-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.15 }
              }
            }}
          >
            {complianceContent.regulations.items.map((item, index) => {
              const icons = [FileCheck, Shield, Lock, Award];
              const Icon = icons[index];
              const colors = ["#667eea", "#f5576c", "#4facfe", "#43e97b"];

              return (
                <motion.div
                  key={index}
                  className="regulatory-card"
                  variants={{
                    hidden: { y: 60, opacity: 0, scale: 0.8 },
                    visible: { 
                      y: 0, 
                      opacity: 1, 
                      scale: 1,
                      transition: { type: "spring", bounce: 0.5 }
                    }
                  }}
                  whileHover={{ y: -15, scale: 1.05 }}
                >
                  <div className="reg-bg-glow" style={{ background: `radial-gradient(circle, ${colors[index]}33, transparent)` }}></div>
                  <div className="reg-icon" style={{ background: `linear-gradient(135deg, ${colors[index]}, ${colors[index]}99)` }}>
                    <Icon size={40} />
                  </div>
                  <h3 className="heading-2">{item.regulation}</h3>
                  <p className="body-medium">{item.description}</p>
                  <motion.div 
                    className="reg-check-badge"
                    style={{ background: colors[index] }}
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                  >
                    <CheckCircle size={24} />
                  </motion.div>
                  <div className="reg-shine"></div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* New Section: Compliance by Design */}
      <section className="principles-section" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(0, 245, 212, 0.05) 0%, transparent 60%), #050608' }}>
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label-center">Architecture</span>
            <h2 className="display-medium">{complianceContent.complianceByDesign.title}</h2>
            <p className="section-subtitle">{complianceContent.complianceByDesign.subtitle}</p>
          </motion.div>

          <div className="principles-grid">
            {complianceContent.complianceByDesign.items.map((cbd, i) => {
              const cbdIcons = [<Building2 size={32} />, <Cpu size={32} />, <Lock size={32} />, <Layers size={32} />, <Database size={32} />, <Scale size={32} />];
              return (
                <motion.div
                  key={i}
                  className="principle-card"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                >
                  <div className="principle-icon">{cbdIcons[i % cbdIcons.length]}</div>
                  <h3>{cbd.title}</h3>
                  <p>{cbd.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Compliance Pillars Section */}
      <section className="principles-section">
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label-center">Governance</span>
            <h2 className="display-medium">{complianceContent.pillars.title}</h2>
            <p className="section-subtitle">{complianceContent.pillars.subtitle}</p>
          </motion.div>

          <div className="principles-grid">
            {complianceContent.pillars.items.map((pillar, i) => {
              const pillarIcons = [<Building2 size={32} />, <Shield size={32} />, <Lock size={32} />, <Database size={32} />, <Cpu size={32} />, <Scale size={32} />];
              return (
                <motion.div
                  key={i}
                  className="principle-card"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                >
                  <div className="principle-icon">{pillarIcons[i % pillarIcons.length]}</div>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disclaimers - Interactive */}
      <section className="disclaimers-section-ultra">
        <div className="container-ultra">
          <motion.div
            className="disclaimers-mega-box"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="disclaimers-glow"></div>
            
            <div className="disclaimers-header-mega">
              <motion.div 
                className="alert-icon-mega"
                animate={{ 
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.05, 1]
                }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <AlertTriangle size={72} />
              </motion.div>
              <h2 className="display-medium">Important Legal Disclaimers</h2>
              <p className="body-large">Statements regarding our corporate model, activities, and operational scope</p>
            </div>

            <div className="disclaimers-list-ultra">
              {complianceContent.disclaimers.map((disclaimer, index) => (
                <motion.div
                  key={index}
                  className={`disclaimer-item-ultra ${activeDisclaimer === index ? 'active' : ''}`}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ x: 10 }}
                  onClick={() => setActiveDisclaimer(activeDisclaimer === index ? null : index)}
                >
                  <div className="disclaimer-number">{String(index + 1).padStart(2, '0')}</div>
                  <div className="disclaimer-content-wrapper">
                    <p className="body-medium disclaimer-text">{disclaimer}</p>
                  </div>
                  <motion.div 
                    className="disclaimer-arrow"
                    animate={{ rotate: activeDisclaimer === index ? 90 : 0 }}
                  >
                    <ChevronRight size={24} />
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Escalation Matrix - Premium */}
      <section className="escalation-section-ultra">
        <div className="container-ultra">
          <motion.div
            className="escalation-mega-container"
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="escalation-bg-pattern"></div>

            <div className="escalation-header-mega">
              <Users size={56} className="escalation-icon" />
              <h2 className="display-medium">{contactInfo.escalationMatrix.title}</h2>
              <p className="body-large">{contactInfo.escalationMatrix.description}</p>
            </div>

            <motion.div 
              className="ceo-contact-mega"
              whileHover={{ scale: 1.01 }}
            >
              <div className="ceo-avatar-escalation-clean">
                <div className="avatar-inner-esc-clean">
                  <Users size={70} />
                </div>
                <div className="avatar-badge">
                  <Shield size={20} />
                </div>
              </div>

              <div className="ceo-details-grid">
                {[
                  { icon: <Users size={24} />, label: "Name", value: contactInfo.escalationMatrix.ceo.name },
                  { icon: <Briefcase size={24} />, label: "Position", value: contactInfo.escalationMatrix.ceo.title },
                  { icon: <Mail size={24} />, label: "Email", value: contactInfo.escalationMatrix.ceo.email, link: `mailto:${contactInfo.escalationMatrix.ceo.email}` },
                  { icon: <Phone size={24} />, label: "Contact", value: contactInfo.escalationMatrix.ceo.phone, link: `tel:${contactInfo.escalationMatrix.ceo.phone}` }
                ].map((item, i) => (
                  <motion.div 
                    key={i} 
                    className="ceo-detail-item"
                    whileHover={{ x: 5 }}
                  >
                    <span className="detail-icon">{item.icon}</span>
                    <div>
                      <span className="detail-label">{item.label}</span>
                      {item.link ? (
                        <a href={item.link} className="detail-value link">{item.value}</a>
                      ) : (
                        <span className="detail-value">{item.value}</span>
                      )}
                    </div>
                  </motion.div>
                ))}

                <div className="ceo-detail-item full-width availability">
                  <Clock size={24} className="detail-icon" />
                  <div>
                    <span className="detail-label">Availability</span>
                    <span className="detail-value">{contactInfo.escalationMatrix.availability}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Regulatory Bodies */}
            <div className="regulatory-bodies-section">
              <h3 className="heading-1 escalate-title">
                <Shield size={36} />
                {contactInfo.escalationMatrix.escalateTitle}
              </h3>
              <p className="body-medium escalate-desc">{contactInfo.escalationMatrix.escalateDescription}</p>

              <div className="regulatory-links-grid">
                {contactInfo.escalationMatrix.regulatoryBodies.map((body, index) => (
                  <motion.a
                    key={index}
                    href={body.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="regulatory-link-card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -8, scale: 1.05 }}
                  >
                    <ExternalLink size={20} />
                    <span>{body.name}</span>
                  </motion.a>
                ))}
              </div>

              <motion.div 
                className="escalate-note"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <AlertCircle size={20} />
                <p className="body-small">{contactInfo.escalationMatrix.note}</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust & Verification */}
      <section className="trust-verification-section">
        <div className="container-ultra">
          <motion.div
            className="section-header-ultra"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label-center">Verification</span>
            <h2 className="display-medium">Verified & Trusted</h2>
            <p className="section-subtitle">Official company information</p>
          </motion.div>

          <div className="trust-grid-mega">
            {[
              { icon: <FileText size={36} />, label: "CIN", value: companyInfo.cin },
              { icon: <Building2 size={36} />, label: "Registered Office", value: companyInfo.registeredOffice },
              { icon: <Phone size={36} />, label: "Official Contact", value: companyInfo.phone },
              { icon: <Mail size={36} />, label: "Compliance Email", value: companyInfo.email }
            ].map((item, i) => (
              <motion.div
                key={i}
                className="trust-info-card"
                initial={{ scale: 0, rotate: -90 }}
                whileInView={{ scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, type: "spring", bounce: 0.5 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="trust-icon">{item.icon}</div>
                <span className="trust-label">{item.label}</span>
                <span className="trust-value">{item.value}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComplianceUltra;
